// ==================================================================================
// Digital Approval System (DAS) - Role Permissions Service
// ==================================================================================
// Axios calls for /admin/role-permissions.
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse, PagedResult } from '../../types/common.types';
import type { ID } from '../../types';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionMerged,
  RolePermissionFilterParams,
} from '../../types/admin/rolePermissions.types';
import { rolePermissionsEndpoints } from '../../endpoints/admin/rolePermissions.endpoints';

export const rolePermissionsService = {
  /** Paginated list. */
  list: async (
    params?: RolePermissionFilterParams
  ): Promise<PagedResult<RolePermission>> => {
    const response = await axiosClient.get<ApiResponse<PagedResult<RolePermission>>>(
      rolePermissionsEndpoints.list(params)
    );
    return response.data.data;
  },

  /** Single row by id. */
  getById: async (id: ID): Promise<RolePermission> => {
    const response = await axiosClient.get<ApiResponse<RolePermission>>(
      rolePermissionsEndpoints.getById(id)
    );
    return response.data.data;
  },

  /**
   * Merge of visible modules + per-module actions across the supplied role
   * codes. Called by useAuth at login to compute the effective permissions
   * for the current user.
   */
  getByRoles: async (rolesCsv: string): Promise<RolePermissionMerged> => {
    const response = await axiosClient.get<ApiResponse<RolePermissionMerged>>(
      rolePermissionsEndpoints.getByRoles(rolesCsv)
    );
    return response.data.data;
  },

  /** Create a new row. */
  create: async (
    payload: CreateRolePermissionRequest
  ): Promise<RolePermission> => {
    const response = await axiosClient.post<ApiResponse<RolePermission>>(
      rolePermissionsEndpoints.create(),
      payload
    );
    return response.data.data;
  },

  /** Update an existing row (rowVersion required for optimistic concurrency). */
  update: async (
    id: ID,
    payload: UpdateRolePermissionRequest
  ): Promise<RolePermission> => {
    const response = await axiosClient.put<ApiResponse<RolePermission>>(
      rolePermissionsEndpoints.update(id),
      payload
    );
    return response.data.data;
  },

  /** Soft delete. System roles return 403. */
  delete: async (id: ID): Promise<void> => {
    await axiosClient.delete<ApiResponse<unknown>>(
      rolePermissionsEndpoints.delete(id)
    );
  },
};
